import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  Schedule,
  Send,
  Business,
  Person,
  WhatsApp,
} from "@mui/icons-material";
import PublicHeaderDark from "components/PublicHeaderDark";
import PublicFooterDark from "components/PublicFooterDark";
import RevealOnScroll from "components/RevealOnScroll";
import DarkHeroBackdrop from "components/DarkHeroBackdrop";
import { SG, BG, BODY_FONT, PANEL, TEXT, PURPLE, PURPLE_LIGHT, MAXW } from "theme/publicDarkTokens";
import SliderCaptcha from "./SliderCaptcha";

// Light-island form input style (matches the established AuthLayout pattern: light
// card background, since this form + SliderCaptcha need clear contrast and the
// captcha widget isn't restyled for dark backgrounds).
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#faf9ff",
    "& fieldset": { borderColor: "#e5dff5" },
    "&:hover fieldset": { borderColor: "#d8cdf0" },
    "&.Mui-focused fieldset": { borderColor: PURPLE },
  },
};

const ContactPage = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    whatsapp: "",
    subject: "",
    message: "",
    serviceType: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState(null);
  const [captchaStatus, setCaptchaStatus] = useState("idle"); // idle|ok|fail
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer(null);
    setCaptchaStatus("idle");
    try {
      const { data } = await axios.get("/api/public/contact/captcha");
      if (data?.success) {
        setCaptcha({ token: data.token, challenge: data.challenge });
      }
    } catch (err) {
      console.error("Failed to load captcha:", err);
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleCaptchaPosition = useCallback((x) => {
    setCaptchaAnswer(x);
    setCaptchaStatus("idle");
  }, []);

  const serviceTypes = [
    t("contact.serviceTypes.crossBorder"),
    t("contact.serviceTypes.claim"),
    t("contact.serviceTypes.homeDelivery"),
    t("contact.serviceTypes.partnership"),
    t("contact.serviceTypes.generalInquiry"),
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!formData.email && !formData.whatsapp) {
      setErrorMessage(t("contact.form.contactRequired"));
      return;
    }
    if (captchaAnswer === null || captchaAnswer === undefined) {
      setErrorMessage(t("contact.form.captchaRequired"));
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post("/api/public/contact/submit", {
        ...formData,
        captchaToken: captcha?.token,
        captchaAnswer,
      });

      if (data?.success) {
        setCaptchaStatus("ok");
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          whatsapp: "",
          subject: "",
          message: "",
          serviceType: "",
        });
        setTimeout(() => {
          setIsSubmitted(false);
          loadCaptcha();
        }, 6000);
      } else {
        setCaptchaStatus("fail");
        setErrorMessage(data?.message || t("contact.form.errorMessage"));
        loadCaptcha();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || t("contact.form.errorMessage");
      const isCaptchaErr = /captcha/i.test(msg);
      setCaptchaStatus(isCaptchaErr ? "fail" : "idle");
      setErrorMessage(msg);
      if (isCaptchaErr) loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone sx={{ fontSize: 32, color: PURPLE_LIGHT }} />,
      title: t("contact.info.phone"),
      details: t("contact.info.phoneNumbers", { returnObjects: true }),
      subtitle: t("contact.info.phoneSubtitle")
    },
    {
      icon: <Email sx={{ fontSize: 32, color: PURPLE_LIGHT }} />,
      title: t("contact.info.email"),
      details: t("contact.info.emails", { returnObjects: true }),
      subtitle: t("contact.info.emailSubtitle")
    },
    {
      icon: <LocationOn sx={{ fontSize: 32, color: PURPLE_LIGHT }} />,
      title: t("contact.info.office"),
      details: t("contact.info.address", { returnObjects: true }),
      subtitle: t("contact.info.officeSubtitle")
    },
    {
      icon: <Schedule sx={{ fontSize: 32, color: PURPLE_LIGHT }} />,
      title: t("contact.info.businessHours"),
      details: t("contact.info.hours", { returnObjects: true }),
      subtitle: t("contact.info.businessHoursSubtitle")
    }
  ];

  const branches = [
    {
      key: "phontong",
      coords: { lat: 17.9928224, lng: 102.6139452 },
      isMain: true,
    },
    {
      key: "thongkang",
      coords: { lat: 17.9339243, lng: 102.6251481 },
      isMain: false,
    },
    {
      key: "dongkhamxang",
      coords: { lat: 17.9008824, lng: 102.6684584 },
      isMain: false,
    },
    {
      key: "savannakhet",
      coords: { lat: 16.5769569, lng: 104.7589554 },
      isMain: false,
    },
  ].map((b) => ({
    ...b,
    city: t(`contact.branches.${b.key}`),
    address: t(`contact.branches.addresses.${b.key}`),
    phone: t(`contact.branches.phones.${b.key}`),
    hours: t(`contact.branches.hours.${b.key}`),
    mapsUrl: `https://www.google.com/maps/search/?api=1&query=${b.coords.lat},${b.coords.lng}`,
  }));

  const mainBranch = branches.find((b) => b.isMain) || branches[0];

  return (
    <Box sx={{ background: BG, fontFamily: BODY_FONT, overflowX: "hidden" }}>
      <PublicHeaderDark />

      {/* ══════════ HERO ══════════ */}
      <Box component="section" sx={{ position: "relative", overflow: "hidden", background: BG, textAlign: "center" }}>
        <DarkHeroBackdrop />
        <Box sx={{ position: "relative", maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 }, py: { xs: 8, md: 11 } }}>
          <RevealOnScroll y={28} duration={700}>
            <Typography
              component="h1"
              sx={{
                fontFamily: SG,
                fontSize: "clamp(32px, 4vw, 52px)",
                fontWeight: 700,
                letterSpacing: "-.03em",
                mb: 2.25,
                background: "linear-gradient(130deg, #C084FC 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("contact.title")}
            </Typography>
            <Typography sx={{ fontSize: 17, color: "rgba(248,245,255,.56)", maxWidth: 600, mx: "auto" }}>
              {t("contact.subtitle")}
            </Typography>
          </RevealOnScroll>
        </Box>
      </Box>

      {/* ══════════ WHATSAPP CTA ══════════ */}
      <Box component="section" sx={{ background: BG, pt: { xs: 3, md: 4 } }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} delay={100}>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "20px",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 3,
                background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                color: "#fff",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <WhatsApp sx={{ fontSize: { xs: 40, md: 56 } }} />
                <Box>
                  <Typography sx={{ fontFamily: SG, fontSize: { xs: 18, md: 21 }, fontWeight: 700 }}>
                    {t("contact.whatsappCta.title")}
                  </Typography>
                  <Typography sx={{ fontSize: 14, opacity: 0.95, mt: 0.5 }}>
                    {t("contact.whatsappCta.subtitle")}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<WhatsApp />}
                onClick={() =>
                  window.open(
                    `https://wa.me/8562077790708?text=${encodeURIComponent(
                      t("contact.whatsappCta.prefill")
                    )}`,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                sx={{
                  flexShrink: 0,
                  borderRadius: "12px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: "none",
                  backgroundColor: "#fff",
                  color: "#128C7E",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#F0EAFF" },
                }}
              >
                {t("contact.whatsappCta.button")}
              </Button>
            </Box>
          </RevealOnScroll>
        </Box>
      </Box>

      {/* ══════════ CONTACT FORM (light island) + INFO ══════════ */}
      <Box component="section" id="contact-form" sx={{ background: BG, py: { xs: 6, md: 9 }, scrollMarginTop: "80px" }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <Grid container spacing={5}>
            {/* Contact Form */}
            <Grid item xs={12} lg={8}>
              <RevealOnScroll y={24} delay={150}>
                <Box
                  sx={{
                    p: { xs: 3, md: 4.5 },
                    borderRadius: "20px",
                    background: "#fff",
                    border: "1.5px solid rgba(124,58,237,.18)",
                    boxShadow: "0 24px 64px rgba(0,0,0,.4), 0 0 56px rgba(124,58,237,.14)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #2D1B69 0%, #4C1D95 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        "& svg": { fontSize: 19, color: "#fff" },
                      }}
                    >
                      <Send />
                    </Box>
                    <Typography sx={{ fontFamily: SG, fontSize: 26, fontWeight: 700, color: "#0A0618" }}>
                      {t("contact.form.title")}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, color: "rgba(10,6,24,.55)", mb: 3.5, ml: { md: "56px" } }}>
                    {t("contact.form.subtitle")}
                  </Typography>

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          sx={fieldSx}
                          label={t("contact.form.fullName")}
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          InputProps={{
                            startAdornment: <Person sx={{ color: "action.active", mr: 1 }} />,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          sx={fieldSx}
                          label={t("contact.form.emailAddress")}
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          helperText={t("contact.form.eitherFieldHelper")}
                          InputProps={{
                            startAdornment: <Email sx={{ color: "action.active", mr: 1 }} />,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          sx={fieldSx}
                          label={t("contact.form.whatsappNumber")}
                          name="whatsapp"
                          value={formData.whatsapp}
                          onChange={handleInputChange}
                          placeholder="+856 20 ..."
                          helperText={t("contact.form.eitherFieldHelper")}
                          InputProps={{
                            startAdornment: <WhatsApp sx={{ color: "action.active", mr: 1 }} />,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth sx={fieldSx}>
                          <InputLabel>{t("contact.form.serviceType")}</InputLabel>
                          <Select
                            name="serviceType"
                            value={formData.serviceType}
                            onChange={handleInputChange}
                            label={t("contact.form.serviceType")}
                            startAdornment={<Business sx={{ color: "action.active", mr: 1 }} />}
                          >
                            {serviceTypes.map((service) => (
                              <MenuItem key={service} value={service}>
                                {service}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          sx={fieldSx}
                          label={t("contact.form.subject")}
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          sx={fieldSx}
                          label={t("contact.form.message")}
                          name="message"
                          multiline
                          rows={6}
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          placeholder={t("contact.form.messagePlaceholder")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <SliderCaptcha
                          data={captcha}
                          loading={captchaLoading}
                          onRefresh={loadCaptcha}
                          onPositionChange={handleCaptchaPosition}
                          status={captchaStatus}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={submitting || captchaLoading}
                          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                          sx={{
                            borderRadius: "12px",
                            px: 4,
                            py: 1.5,
                            textTransform: "none",
                            fontWeight: 700,
                            background: PURPLE,
                            boxShadow: "none",
                            "&:hover": { background: "#6D28D9" },
                          }}
                        >
                          {submitting ? t("contact.form.sending") : t("contact.form.sendMessage")}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        {isSubmitted && (
                          <Alert severity="success" sx={{ mb: 2 }}>
                            {t("contact.form.successMessage")}
                          </Alert>
                        )}
                        {errorMessage && (
                          <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            onClose={() => setErrorMessage("")}
                          >
                            {errorMessage}
                          </Alert>
                        )}
                        <Alert severity="info">
                          {t("contact.form.contactHint")}
                        </Alert>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </RevealOnScroll>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {contactInfo.map((info, index) => (
                  <RevealOnScroll key={index} y={20} delay={index * 80}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: "16px",
                        background: PANEL,
                        border: "1.5px solid rgba(248,245,255,.08)",
                        transition: "all .3s ease",
                        "&:hover": { transform: "translateY(-4px)", borderColor: "rgba(124,58,237,.4)" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Box sx={{ mt: 0.5 }}>{info.icon}</Box>
                        <Box>
                          <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 16, color: TEXT, mb: 0.5 }}>
                            {info.title}
                          </Typography>
                          {info.details.map((detail, idx) => (
                            <Typography key={idx} sx={{ fontSize: 14, color: "rgba(248,245,255,.75)", mb: 0.5 }}>
                              {detail}
                            </Typography>
                          ))}
                          <Typography sx={{ fontSize: 13, color: "rgba(248,245,255,.4)" }}>
                            {info.subtitle}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </RevealOnScroll>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ══════════ BRANCH LOCATIONS ══════════ */}
      <Box component="section" id="contact-branches" sx={{ background: PANEL, py: { xs: 6, md: 9 }, scrollMarginTop: "80px" }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 6 }}>
            <Typography sx={{ fontFamily: SG, fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: TEXT, mb: 1 }}>
              {t("contact.branches.title")}
            </Typography>
            <Typography sx={{ fontSize: 16, color: "rgba(248,245,255,.45)" }}>{t("contact.branches.subtitle")}</Typography>
          </RevealOnScroll>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4,1fr)" }, gap: 2.5 }}>
            {branches.map((branch, index) => (
              <RevealOnScroll key={index} y={24} delay={index * 80}>
                <Box
                  sx={{
                    p: 3,
                    height: "100%",
                    position: "relative",
                    borderRadius: "16px",
                    background: BG,
                    border: branch.isMain ? "1.5px solid rgba(124,58,237,.5)" : "1.5px solid rgba(248,245,255,.08)",
                    transition: "all .3s ease",
                    "&:hover": { transform: "translateY(-4px)", borderColor: "rgba(124,58,237,.5)" },
                  }}
                >
                  {branch.isMain && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "linear-gradient(135deg, #7C3AED 0%, #C084FC 100%)",
                        color: "white",
                        px: 1.5,
                        py: 0.6,
                        borderRadius: "8px",
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.6px",
                        textTransform: "uppercase",
                      }}
                    >
                      {t("contact.branches.mainOffice")}
                    </Box>
                  )}
                  <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 17, color: PURPLE_LIGHT, mb: 1.5 }}>
                    {branch.city}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
                    <LocationOn sx={{ fontSize: 18, color: "rgba(248,245,255,.4)", mt: 0.2 }} />
                    <Typography sx={{ fontSize: 13, color: "rgba(248,245,255,.55)" }}>{branch.address}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Phone sx={{ fontSize: 18, color: "rgba(248,245,255,.4)" }} />
                    <Typography sx={{ fontSize: 13, color: "rgba(248,245,255,.55)" }}>{branch.phone}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Schedule sx={{ fontSize: 18, color: "rgba(248,245,255,.4)" }} />
                    <Typography sx={{ fontSize: 13, color: "rgba(248,245,255,.55)" }}>{branch.hours}</Typography>
                  </Box>
                  <Button
                    fullWidth
                    startIcon={<LocationOn />}
                    onClick={() => window.open(branch.mapsUrl, "_blank", "noopener,noreferrer")}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      color: TEXT,
                      border: "1.5px solid rgba(248,245,255,.18)",
                      "&:hover": { borderColor: "rgba(168,85,247,.65)", background: "rgba(124,58,237,.1)" },
                    }}
                  >
                    {t("contact.branches.openInMaps")}
                  </Button>
                </Box>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════ MAP ══════════ */}
      <Box component="section" sx={{ background: BG, py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 4 }}>
            <Typography sx={{ fontFamily: SG, fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: TEXT, mb: 1 }}>
              {t("contact.map.title")}
            </Typography>
            <Typography sx={{ fontSize: 15, color: "rgba(248,245,255,.45)" }}>{t("contact.map.subtitle")}</Typography>
          </RevealOnScroll>
          <RevealOnScroll y={24} delay={100}>
            <Box
              sx={{
                height: { xs: 320, md: 440 },
                borderRadius: "20px",
                overflow: "hidden",
                border: "1.5px solid rgba(124,58,237,.25)",
                position: "relative",
                boxShadow: "0 24px 64px rgba(0,0,0,.4)",
              }}
            >
              <iframe
                title={`Winner Express — ${mainBranch.city}`}
                src={`https://www.google.com/maps?q=${mainBranch.coords.lat},${mainBranch.coords.lng}&z=17&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <Button
                variant="contained"
                startIcon={<LocationOn />}
                onClick={() => window.open(mainBranch.mapsUrl, "_blank", "noopener,noreferrer")}
                sx={{
                  position: "absolute",
                  bottom: 16,
                  right: 16,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  background: PURPLE,
                  boxShadow: "0 8px 24px rgba(0,0,0,.35)",
                  "&:hover": { background: "#6D28D9" },
                }}
              >
                {t("contact.map.viewOnGoogleMaps")}
              </Button>
            </Box>
          </RevealOnScroll>
        </Box>
      </Box>

      {/* ══════════ FAQ ══════════ */}
      <Box component="section" id="contact-faq" sx={{ background: PANEL, py: { xs: 6, md: 9 }, scrollMarginTop: "80px" }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 6 }}>
            <Typography sx={{ fontFamily: SG, fontSize: { xs: 24, md: 30 }, fontWeight: 700, color: TEXT, mb: 1 }}>
              {t("contact.faq.title")}
            </Typography>
            <Typography sx={{ fontSize: 16, color: "rgba(248,245,255,.45)" }}>{t("contact.faq.subtitle")}</Typography>
          </RevealOnScroll>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
            {[
              { q: "contact.faq.deliveryTimes.question", a: "contact.faq.deliveryTimes.answer" },
              { q: "contact.faq.tracking.question", a: "contact.faq.tracking.answer" },
              { q: "contact.faq.payment.question", a: "contact.faq.payment.answer" },
              { q: "contact.faq.freeClaim.question", a: "contact.faq.freeClaim.answer" },
            ].map((faq, index) => (
              <RevealOnScroll key={index} y={20} delay={index * 70}>
                <Box sx={{ p: 3, height: "100%", borderRadius: "16px", background: BG, border: "1.5px solid rgba(248,245,255,.08)" }}>
                  <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 16, color: TEXT, mb: 1 }}>{t(faq.q)}</Typography>
                  <Typography sx={{ fontSize: 14, color: "rgba(248,245,255,.55)", lineHeight: 1.7 }}>{t(faq.a)}</Typography>
                </Box>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      <PublicFooterDark />
    </Box>
  );
};

export default ContactPage;
